import React from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Chip,
  Autocomplete,
  Paper,
  SelectChangeEvent
} from '@mui/material';
import { VenueFormData, VenueType } from '../../../types/Venue';

interface VenueBasicInfoFormProps {
  venueData: VenueFormData;
  updateVenueData: (data: Partial<VenueFormData>) => void;
}

// Common amenities to choose from
const COMMON_AMENITIES = [
  'Free WiFi', 
  'Parking', 
  'Outdoor Seating', 
  'Private Dining', 
  'Wheelchair Accessible',
  'Bar', 
  'Live Music', 
  'Kid Friendly', 
  'Pet Friendly', 
  'Vegan Options',
  'Gluten-Free Options', 
  'Reservations', 
  'Delivery', 
  'Takeout',
  'BYOB', 
  'Full Bar', 
  'Wine List', 
  'Happy Hour',
  'Catering', 
  'Private Events', 
  'Meeting Rooms', 
  'AV Equipment',
  'Swimming Pool', 
  'Spa Services', 
  'Fitness Center', 
  'Business Center',
  'Airport Shuttle', 
  'Concierge Service', 
  'Room Service',
  'Scenic View', 
  'Waterfront',
  'Garden/Patio', 
  'Fireplace'
];

const VenueBasicInfoForm: React.FC<VenueBasicInfoFormProps> = ({ venueData, updateVenueData }) => {
  const handleTypeChange = (event: SelectChangeEvent<VenueType>) => {
    updateVenueData({ type: event.target.value as VenueType });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Basic Venue Information
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Venue Name"
            value={venueData.name}
            onChange={(e) => updateVenueData({ name: e.target.value })}
            helperText="Enter the official name of your venue"
            variant="outlined"
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel id="venue-type-label">Venue Type</InputLabel>
            <Select
              labelId="venue-type-label"
              value={venueData.type}
              onChange={handleTypeChange}
              label="Venue Type"
            >
              <MenuItem value="restaurant">Restaurant</MenuItem>
              <MenuItem value="event_space">Event Space</MenuItem>
              <MenuItem value="resort">Resort</MenuItem>
            </Select>
            <FormHelperText>Select the type that best describes your venue</FormHelperText>
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Description"
            value={venueData.description}
            onChange={(e) => updateVenueData({ description: e.target.value })}
            multiline
            rows={4}
            helperText="Describe your venue, its atmosphere, and what makes it special"
            variant="outlined"
          />
        </Grid>
        
        <Grid item xs={12}>
          <Autocomplete
            multiple
            id="amenities-tags"
            options={COMMON_AMENITIES}
            freeSolo
            value={venueData.amenities}
            onChange={(event, newValue) => {
              updateVenueData({ amenities: newValue });
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Amenities"
                helperText="Select or type amenities your venue offers"
                placeholder="Add amenity"
              />
            )}
          />
        </Grid>
      </Grid>
      
      {/* Business Hours Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Business Hours
        </Typography>
        
        <Paper elevation={1} sx={{ p: 2, bgcolor: 'background.default' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Default hours are set for your convenience. You can adjust these in the venue settings after creating your venue.
          </Typography>
          
          <Grid container spacing={2}>
            {venueData.businessHours.map((hours, index) => {
              const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', width: '100px' }}>
                      {days[hours.dayOfWeek]}:
                    </Typography>
                    <Typography variant="body2" color={hours.isClosed ? "text.secondary" : "text.primary"}>
                      {hours.isClosed ? 'Closed' : `${hours.open} - ${hours.close}`}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default VenueBasicInfoForm;
